import { defineStore } from 'pinia'
import apiClient from '@/api/client'
import { getApiBaseUrl } from '@/api/base-url'
import { ref, computed } from 'vue'
import type { Product, Category } from '@/types/catalog'
import { isFeatureEnabled } from '@/modules/common/public'

interface FilterValue {
  value: string
  label: string
}

interface Filter {
  id: string
  name: string
  values: FilterValue[]
}

type AttributeFilterDescriptor = {
  id: number
  name: string
  type: 'string' | 'list' | 'number' | 'boolean'
  unit?: string | null
  description?: string | null
  options?: Array<string | { value: boolean; label: string }>
  range?: {
    min: number | null
    max: number | null
  }
}

// Ответ API может быть в snake_case (backend Symfony)
type ApiProduct = Record<string, unknown> & {
  id?: number | string
  sku?: string
  name?: string
  description?: string
  price?: unknown
  stock?: number
  is_available?: boolean
  isAvailable?: boolean
  minimum_order?: number | null
  is_bestseller?: boolean
  isBestseller?: boolean
  primary_image_url?: string
  images?: string[]
  category?: { id?: number; name?: string; slug?: string } | null
  manufacturer?: { id?: number; name?: string; slug?: string } | null
}

/**
 * Хранилище для каталога продуктов
 * Используется для получения списка продуктов, категорий, фильтрации и сортировки
 */
export const useCatalogStore = defineStore('catalog', () => {
  // Состояние
  const products = ref<Product[]>([])
  const categories = ref<Category[]>([])
  const manufacturers = ref<{ id: string; name: string; slug?: string }[]>([])
  const filters = ref<Filter[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)
  const totalCount = ref(0)
  const currentPage = ref(1)
  const itemsPerPage = ref(20)
  const searchQuery = ref('')
  const selectedCategory = ref<string | null>(null)
  const selectedFilters = ref<Record<string, FilterValue[]>>({})
  const selectedSupplier = ref<string | null>(null)
  const selectedManufacturers = ref<string[]>([])
  const priceRange = ref<[number, number]>([0, 1000000])
  const onlyInStock = ref(false)
  const suppliers = ref<{ id: string; name: string }[]>([])
  const categoryAttributeFilters = ref<AttributeFilterDescriptor[]>([])
  const attributeFilterParams = ref<Record<string, string | string[]>>({})
  const sortBy = ref('name')
  const sortOrder = ref('asc')

  // Геттеры
  const filteredProducts = computed(() => {
    return products.value
  })

  const paginationInfo = computed(() => {
    return {
      currentPage: currentPage.value,
      totalPages: Math.ceil(totalCount.value / itemsPerPage.value),
      totalItems: totalCount.value,
      itemsPerPage: itemsPerPage.value,
    }
  })

  /** Не показывать в UI slug/ID вместо названия (длинные hex, UUID и т.п.) */
  function displayName(value: string | undefined | null): string {
    if (value == null || typeof value !== 'string') return ''
    const trimmed = value.trim()
    if (trimmed.length === 0) return ''

    const withoutTechnicalTail = trimmed
      .replace(/\b[0-9a-f]{12,}\b/gi, '')
      .replace(/\b[0-9a-f]{8}-[0-9a-f-]{27,}\b/gi, '')
      .replace(/\s{2,}/g, ' ')
      .trim()

    if (withoutTechnicalTail.length === 0) return '-'
    if (/^[a-f0-9]{12,}$/i.test(withoutTechnicalTail)) return '-'
    if (/^[a-f0-9-]{24,}$/i.test(withoutTechnicalTail)) return '-'
    if (/^\d+$/.test(withoutTechnicalTail) && withoutTechnicalTail.length > 8) return '-'

    return withoutTechnicalTail
  }

  function displaySku(value: string | undefined | null): string {
    if (value == null || typeof value !== 'string') return '-'

    const trimmed = value.trim()
    if (trimmed.length === 0) return '-'

    const cleaned = trimmed
      .replace(/-?[0-9a-f]{12,}$/i, '')
      .replace(/[0-9a-f]{12,}/gi, '')
      .replace(/-{2,}/g, '-')
      .replace(/-\s*$/g, '')
      .trim()

    return cleaned.length > 0 ? cleaned : '-'
  }

  // Функция для преобразования данных из API в формат фронтенда
  function transformApiProduct(apiProduct: ApiProduct): Product {
    const raw = apiProduct as Record<string, unknown>
    const apiBaseUrl = getApiBaseUrl()
    const isSeededImagePlaceholder = (img: string): boolean => {
      const normalized = img.split('/').pop() ?? img
      // product__main.jpg, product__2.jpg - без id (двойное подчёркивание)
      // product_123_main.jpg, microscope_121_main.jpg - с id (одиночные суффиксы)
      return (
        /^(product|microscope|centrifuge|spectrophotometer|chromatograph|pipette|filter|scale)_\d*_(main|stage|rotor|module|cell|tip|panel|detail|\d+)\.jpg$/i.test(
          normalized,
        ) || /^product__[\w]+\.jpg$/i.test(normalized)
      )
    }
    const normalizeImageUrl = (img: string): string => {
      if (!img) return ''
      if (isSeededImagePlaceholder(img)) return '/placeholder-product.svg'
      if (img.startsWith('http')) return img
      if (img.startsWith('/')) return `${apiBaseUrl}${img}`
      return `${apiBaseUrl}/uploads/products/images/${img}`
    }
    // Безопасное преобразование строк в числа
    const parseNumber = (value: unknown, defaultValue = 0): number => {
      // Если уже число, возвращаем как есть
      if (typeof value === 'number') {
        return isNaN(value) ? defaultValue : value
      }
      // Если строка, пытаемся распарсить
      if (typeof value === 'string') {
        // Убираем пробелы и проверяем на пустую строку
        const trimmed = value.trim()
        if (trimmed === '' || trimmed === 'null' || trimmed === 'undefined') {
          return defaultValue
        }
        const parsed = parseFloat(trimmed)
        return isNaN(parsed) ? defaultValue : parsed
      }
      // Если null или undefined, возвращаем defaultValue
      if (value === null || value === undefined) {
        return defaultValue
      }
      return defaultValue
    }

    const parseInteger = (value: string | number | undefined, defaultValue = 0): number => {
      if (typeof value === 'number') {
        return Math.floor(value)
      }
      if (typeof value === 'string') {
        const parsed = parseInt(value, 10)
        return isNaN(parsed) ? defaultValue : parsed
      }
      return defaultValue
    }

    // Пробуем разные варианты названия поля цены
    // Цена может быть объектом Money {amount: 25000, currency: "RUB"} или числом/строкой
    const apiProductRecord = apiProduct as Record<string, unknown>
    let priceValue: unknown =
      apiProductRecord['price'] ??
      apiProductRecord['unit_price'] ??
      apiProductRecord['base_price'] ??
      apiProductRecord['price_with_tax'] ??
      0

    // Если цена - объект Money, извлекаем amount (в копейках)
    if (priceValue && typeof priceValue === 'object' && !Array.isArray(priceValue)) {
      // Проверяем разные возможные поля объекта Money
      const priceRecord = priceValue as Record<string, unknown>
      const amount =
        (priceRecord['amount'] as number | string | undefined) ??
        (priceRecord['value'] as number | string | undefined) ??
        (priceRecord['price'] as number | string | undefined) ??
        (priceRecord['amountInMinorUnits'] as number | string | undefined) ??
        0

      // amount в копейках, делим на 100 для получения рублей
      if (typeof amount === 'number' && !isNaN(amount)) {
        priceValue = amount / 100
      } else if (typeof amount === 'string') {
        const parsed = parseFloat(amount)
        priceValue = isNaN(parsed) ? 0 : parsed / 100
      } else {
        priceValue = 0
      }
    }

    const price = parseNumber(priceValue, 0)
    const stock = parseInteger(
      (raw['stock'] as number | string | undefined) ?? apiProduct['stock'],
      0,
    )
    const isAvailable = raw['is_available'] ?? raw['isAvailable'] ?? stock > 0
    const minimumOrderValue = raw['minimum_order'] ?? raw['minimumOrder']
    const minimumOrder =
      typeof minimumOrderValue === 'number' &&
      Number.isInteger(minimumOrderValue) &&
      minimumOrderValue > 0
        ? minimumOrderValue
        : undefined

    const category = (apiProduct['category'] as { id?: number; name?: string } | null) ?? null
    const manufacturer =
      (apiProduct['manufacturer'] as { id?: number; name?: string } | null) ?? null
    const categoryName = displayName(category?.name ?? '')
    const manufacturerName = displayName(manufacturer?.name ?? '')
    const rawName = apiProduct['name']
    const productName =
      typeof rawName === 'string' && rawName.trim().length > 0 ? rawName.trim() : 'Без названия'
    const rawSku = apiProduct['sku']
    const sku = displaySku(typeof rawSku === 'string' ? rawSku.trim() : '')

    const primaryImageRaw =
      (raw['primary_image_url'] as string | undefined) ||
      (Array.isArray(raw['images']) && (raw['images'] as string[]).length > 0
        ? ((raw['images'] as string[])[0] as string)
        : null)
    const imagesArray = Array.isArray(raw['images']) ? (raw['images'] as string[]) : []
    const normalizedImages = imagesArray
      .map((img) => normalizeImageUrl(img))
      .filter((img) => img !== '')
    const primaryImage = primaryImageRaw ? normalizeImageUrl(primaryImageRaw) : null
    const imageUrl = primaryImage || normalizedImages[0] || ''
    const images = normalizedImages.length > 0 ? normalizedImages : [imageUrl]

    return {
      id: parseInteger(apiProduct['id'] as number | string | undefined, 0) || 0,
      sku,
      name: productName,
      description: (apiProduct['description'] as string) ?? '',
      shortDescription: '',
      price,
      imageUrl,
      images,
      inStock: Boolean(isAvailable) && stock > 0,
      stockQuantity: stock,
      ...(minimumOrder !== undefined ? { minimumOrder } : {}),
      unit: (() => {
        const u = raw['unit'] as { id?: number; name?: string; symbol?: string } | null | undefined
        if (u && typeof u === 'object') {
          return {
            id: Number(u.id ?? 0),
            name: String(u.name ?? ''),
            symbol: String(u.symbol ?? ''),
          }
        }
        return null
      })(),
      rating: 0,
      reviewCount: 0,
      manufacturerName: manufacturerName || '-',
      categoryName: categoryName || '-',
      specifications: {},
      isNew: false,
      isBestseller: Boolean(raw['is_bestseller'] ?? raw['isBestseller'] ?? false),
    }
  }

  // Действия
  async function fetchProducts() {
    loading.value = true
    error.value = null
    try {
      const apiUrl = getApiBaseUrl()

      const hasAttributeFilters = Object.keys(attributeFilterParams.value).length > 0
      // /api/search (SearchBundle/ES) отдаёт 404, пока advanced_search_enabled выключен.
      // Каталожный поиск - через /api/products?search=; ES только при явном флаге.
      const useAdvancedSearch =
        Boolean(searchQuery.value?.trim()) &&
        !hasAttributeFilters &&
        isFeatureEnabled('advanced_search_enabled')

      if (useAdvancedSearch) {
        await fetchProductsWithSearch(apiUrl)
      } else {
        await fetchProductsRegular(apiUrl)
      }
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Ошибка загрузки товаров'
      console.error('Ошибка при загрузке товаров:', e)
      products.value = []
      totalCount.value = 0
    } finally {
      loading.value = false
    }
  }

  /**
   * Поиск через SearchBundle (Elasticsearch с fallback на PostgreSQL)
   */
  async function fetchProductsWithSearch(apiUrl: string) {
    try {
      const params: Record<string, string | number | string[]> = {
        q: searchQuery.value,
        offset: (currentPage.value - 1) * itemsPerPage.value,
        limit: itemsPerPage.value,
        sort: sortBy.value,
        order: sortOrder.value,
      }

      if (selectedCategory.value) {
        params['categories[]'] = selectedCategory.value
      }

      // Добавляем фильтр по производителям
      if (selectedManufacturers.value && selectedManufacturers.value.length > 0) {
        params['manufacturers[]'] = selectedManufacturers.value
      }

      // Добавляем фильтр по цене
      if (priceRange.value[0] > 0) {
        params['price_min'] = priceRange.value[0]
      }
      if (priceRange.value[1] < 1000000) {
        params['price_max'] = priceRange.value[1]
      }

      const response = await apiClient.get(`${apiUrl}/api/search`, { params })

      if (response.data.success) {
        const searchResults = response.data.data.products as ApiProduct[]
        products.value = searchResults.map(transformApiProduct)
        totalCount.value = response.data.data.total || 0
      } else {
        // Fallback на обычный поиск если SearchBundle недоступен
        await fetchProductsRegular(apiUrl)
      }
    } catch (e) {
      console.warn('SearchBundle unavailable, falling back to regular search:', e)
      // Fallback на обычный поиск
      await fetchProductsRegular(apiUrl)
    }
  }

  /**
   * Обычная загрузка через /api/products
   */
  async function fetchProductsRegular(apiUrl: string) {
    const params: Record<string, string | number | string[]> = {
      page: currentPage.value,
      limit: itemsPerPage.value,
      sort_by: sortBy.value,
      sort_order: sortOrder.value,
    }

    if (searchQuery.value) {
      params['search'] = searchQuery.value
    }

    if (selectedCategory.value) {
      params['category'] = selectedCategory.value
    }

    if (selectedSupplier.value) {
      params['supplier'] = selectedSupplier.value
    }

    // /api/products принимает одного производителя (ProductListFilters::manufacturer_id)
    if (selectedManufacturers.value.length === 1) {
      params['manufacturer'] = selectedManufacturers.value[0] as string
    }

    if (onlyInStock.value) {
      params['in_stock'] = 'true'
    }

    Object.assign(params, attributeFilterParams.value)

    const response = await apiClient.get(`${apiUrl}/api/products`, { params })

    if (response.data.success) {
      const apiProducts = (response.data.data?.products ?? []) as ApiProduct[]
      products.value = apiProducts.map(transformApiProduct)
      const total = response.data.data?.pagination?.total
      totalCount.value =
        typeof total === 'number' ? total : typeof total === 'string' ? parseInt(total, 10) || 0 : 0
    } else {
      throw new Error('API returned unsuccessful response')
    }
  }

  async function fetchCategories() {
    try {
      // Используем абсолютный URL
      const apiUrl = getApiBaseUrl()
      const response = await apiClient.get(`${apiUrl}/api/products/categories`)

      if (response.data.success) {
        categories.value = response.data.data.map(
          (cat: {
            id: number
            name: string
            slug: string
            description?: string
            product_count?: number
            products_count?: number
          }) => ({
            id: cat.id,
            name: cat.name,
            slug: cat.slug,
            description: cat.description || '',
            productCount: cat.products_count ?? cat.product_count ?? 0,
          }),
        )
      } else {
        throw new Error('API returned unsuccessful response')
      }
    } catch (e) {
      console.error('Ошибка при загрузке категорий:', e)
      categories.value = []
    }
  }

  // TODO Post-MVP: реализовать загрузку фильтров из API (SearchBundle)
  async function fetchFilters() {
    filters.value = []
  }

  async function fetchManufacturers() {
    try {
      // Используем абсолютный URL
      const apiUrl = getApiBaseUrl()
      const response = await apiClient.get(`${apiUrl}/api/products/manufacturers`)

      if (response.data.success) {
        manufacturers.value = response.data.data.map(
          (manufacturer: { id: number; name: string; slug: string }) => ({
            id: manufacturer.id.toString(),
            name: manufacturer.name,
            slug: manufacturer.slug,
          }),
        )
      } else {
        throw new Error('API returned unsuccessful response')
      }
    } catch (e) {
      console.error('Ошибка при загрузке производителей:', e)
      manufacturers.value = []
    }
  }

  async function fetchSuppliers() {
    try {
      // Используем абсолютный URL
      const apiUrl = getApiBaseUrl()
      const response = await apiClient.get(`${apiUrl}/api/products/suppliers`)

      if (response.data.success) {
        suppliers.value = response.data.data.map((supplier: { id: number; name: string }) => ({
          id: supplier.id.toString(),
          name: supplier.name,
        }))
      } else {
        throw new Error('API returned unsuccessful response')
      }
    } catch (e) {
      console.error('Ошибка при загрузке поставщиков:', e)
      suppliers.value = []
    }
  }

  async function fetchCategoryAttributeFilters(categoryId: string | null) {
    if (!categoryId) {
      categoryAttributeFilters.value = []
      return
    }

    try {
      const apiUrl = getApiBaseUrl()
      const response = await apiClient.get(`${apiUrl}/api/products/filters`, {
        params: {
          category: categoryId,
        },
      })

      if (response.data.success) {
        categoryAttributeFilters.value = response.data.data as AttributeFilterDescriptor[]
      } else {
        categoryAttributeFilters.value = []
      }
    } catch (e) {
      console.error('Ошибка при загрузке фильтров характеристик:', e)
      categoryAttributeFilters.value = []
    }
  }

  function setCategory(categoryId: string | null, shouldFetch = true) {
    selectedCategory.value = categoryId
    // Сбрасываем фильтры и пагинацию при изменении категории
    selectedFilters.value = {}
    currentPage.value = 1
    // Обновляем список товаров и доступных фильтров
    if (shouldFetch) {
      fetchFilters()
      fetchProducts()
    }
  }

  function setAttributeFilterParams(params: Record<string, string | string[]>, shouldFetch = true) {
    attributeFilterParams.value = { ...params }
    currentPage.value = 1
    if (shouldFetch) {
      fetchProducts()
    }
  }

  function setFilter(filterName: string, value: FilterValue) {
    // Инициализируем массив выбранных значений для фильтра, если его еще нет
    if (!selectedFilters.value[filterName]) {
      selectedFilters.value[filterName] = []
    }

    // Если значение уже выбрано, удаляем его
    const index = selectedFilters.value[filterName].findIndex((v) => v.value === value.value)
    if (index !== -1) {
      selectedFilters.value[filterName].splice(index, 1)
      // Если массив значений стал пустым, удаляем ключ
      if (selectedFilters.value[filterName].length === 0) {
        delete selectedFilters.value[filterName]
      }
    } else {
      // Добавляем новое значение
      selectedFilters.value[filterName].push(value)
    }

    // Сбрасываем пагинацию и обновляем список товаров
    currentPage.value = 1
    fetchProducts()
  }

  function setSorting(sort: string, order: 'asc' | 'desc' = 'asc', shouldFetch = true) {
    // Нормализация значений из UI (name_asc) и прямых полей API (name + asc)
    const normalized = (() => {
      if (sort === 'name_asc') return { by: 'name', order: 'asc' as const }
      if (sort === 'name_desc') return { by: 'name', order: 'desc' as const }
      if (sort === 'price_asc') return { by: 'price', order: 'asc' as const }
      if (sort === 'price_desc') return { by: 'price', order: 'desc' as const }
      if (sort === 'created_desc') return { by: 'created_at', order: 'desc' as const }
      if (sort === 'created_asc') return { by: 'created_at', order: 'asc' as const }
      if (sort === 'popularity_desc') return { by: 'view_count', order: 'desc' as const }
      if (sort === 'popularity_asc') return { by: 'view_count', order: 'asc' as const }
      if (sort === 'popularity') return { by: 'view_count', order: 'desc' as const }

      const allowedBy = new Set(['name', 'price', 'created_at', 'view_count'])
      const allowedOrder = new Set(['asc', 'desc'])

      return {
        by: allowedBy.has(sort) ? sort : 'name',
        order: allowedOrder.has(order) ? order : 'asc',
      } as const
    })()

    sortBy.value = normalized.by
    sortOrder.value = normalized.order

    if (shouldFetch) {
      fetchProducts()
    }
  }

  function setPage(page: number, shouldFetch = true) {
    currentPage.value = page
    if (shouldFetch) {
      fetchProducts()
    }
  }

  function setSearch(query: string, shouldFetch = true) {
    searchQuery.value = query
    currentPage.value = 1
    if (shouldFetch) {
      fetchProducts()
    }
  }

  function setSupplier(supplierId: string | null, shouldFetch = true) {
    selectedSupplier.value = supplierId
    currentPage.value = 1
    if (shouldFetch) {
      fetchProducts()
    }
  }

  function setManufacturers(manufacturerIds: string[], shouldFetch = true) {
    selectedManufacturers.value = [...manufacturerIds]
    currentPage.value = 1
    if (shouldFetch) {
      fetchProducts()
    }
  }

  function setInStock(value: boolean, shouldFetch = true) {
    onlyInStock.value = value
    currentPage.value = 1
    if (shouldFetch) {
      fetchProducts()
    }
  }

  function resetFilters(shouldFetch = true) {
    selectedFilters.value = {}
    selectedSupplier.value = null
    selectedManufacturers.value = []
    attributeFilterParams.value = {}
    onlyInStock.value = false
    sortBy.value = 'name'
    sortOrder.value = 'asc'
    currentPage.value = 1
    if (shouldFetch) {
      fetchProducts()
    }
  }

  // Инициализация данных каталога
  // Товары не грузим здесь: CatalogView сам зовёт fetchProducts через loadProducts()
  // после применения фильтров из query-параметров - иначе получится двойной запрос.
  function initCatalog() {
    return Promise.all([fetchCategories(), fetchManufacturers(), fetchFilters(), fetchSuppliers()])
  }

  return {
    // Состояние
    products,
    categories,
    manufacturers,
    filters,
    suppliers,
    categoryAttributeFilters,
    attributeFilterParams,
    loading,
    error,
    searchQuery,
    selectedCategory,
    selectedFilters,
    selectedSupplier,
    sortBy,
    sortOrder,
    currentPage,
    itemsPerPage,
    totalCount,

    // Геттеры
    filteredProducts,
    paginationInfo,

    // Действия
    fetchProducts,
    fetchCategories,
    fetchManufacturers,
    fetchFilters,
    fetchSuppliers,
    fetchCategoryAttributeFilters,
    setCategory,
    setFilter,
    setSupplier,
    setManufacturers,
    setInStock,
    setAttributeFilterParams,
    setSorting,
    setPage,
    setSearch,
    resetFilters,
    initCatalog,
  }
})
